/**
 * Minhas tarefas (H10).
 *
 * ── O atraso vem do servidor, e é isso que resolve a armadilha ────────────
 *
 * *"Marcar como atrasada toda tarefa vencida no relógio. Segunda de manhã o
 * vendedor encontra vinte tarefas 'atrasadas' que venceram no fim de semana, e o
 * indicador perde o sentido."*
 *
 * A D05 já derivou isso na leitura, com o expediente da revenda: tarefa vencida
 * às 22h de sexta não conta como atrasada até segunda. A tela **mostra** o campo
 * e não recalcula nada — e é justamente por não recalcular que ela acerta. A
 * derivação depende do `horario_de_atendimento` do tenant, que a interface não
 * tem e não deveria ter.
 *
 * ── Concluir com um clique, sem confirmação ───────────────────────────────
 *
 * Ver `conclusao.ts`. O desfazer é real porque o comando não sai durante a
 * janela — não existe rota para reabrir tarefa.
 *
 * ── A prioridade é MOSTRADA, e não reordena a lista ───────────────────────
 *
 * Ver `prioridade.ts`. O eixo continua sendo o prazo: uma tarefa urgente para
 * semana que vem não vem antes de uma normal para hoje.
 *
 * ── O que a task pede e o contrato ainda não tem ──────────────────────────
 *
 * **Vínculo visível** — de qual cliente e oportunidade — não volta na leitura:
 * o `alvo` entra na CRIAÇÃO e a resposta não o devolve. Fica declarado, e
 * aparece na tela como aviso, porque é informação que o vendedor procura e não
 * acha.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { TarefaDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useApi, useTarefas } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { useEsperaDaProjecao } from '@/shared/api/espera-da-projecao';
import { Carregando, Defasado, Erro, Vazio } from '@/shared/ui/estados/estados';
import {
  agendar,
  desfazer,
  JANELA_DE_DESFAZER_MS,
  pareceConcluida,
  segundosRestantes,
  separarProntas,
  type ConclusaoPendente,
} from '../lib/conclusao';
import { CartaoDeTarefa } from './cartao-de-tarefa';
import { agruparPorPrazo, type ChaveDeGrupo, type Grupo } from '../lib/grupos';
import { NovaTarefa } from '@/features/comercial/criar-tarefa';
import './tarefas.css';
import { Botao, CabecalhoDeTela, FiltroPilula } from '@/shared/ui';

export function Tarefas({
  aoAbrirConversa,
  aoAbrirCliente,
}: {
  /** O nome do cliente no cartão leva à conversa dele (#950)… */
  aoAbrirConversa: (conversaId: string) => void;
  /** …ou ao Cliente 360, quando ele ainda não tem conversa. */
  aoAbrirCliente: (clienteId: string) => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();

  const [criando, definirCriando] = useState(false);
  /*
   * Qual cartão está aberto — no ESTADO da aplicação, e não no DOM.
   *
   * Sem isto, um render disparado por qualquer outro motivo (a conclusão de
   * outra tarefa, o SSE, uma revalidação) fecharia o cartão que a pessoa está
   * lendo no meio da leitura.
   */
  const [abertaId, definirAbertaId] = useState<string | null>(null);

  /*
   * A aba aberta. O padrão é HOJE, e não "Atrasadas".
   *
   * Atrasadas é a primeira da fileira porque é a mais urgente — mas abrir nela
   * faria a tela começar vazia na maioria dos dias, que é justamente o desfecho
   * que se quer. "Nada atrasado" é boa notícia, e não um bom lugar para
   * começar.
   *
   * Quem avisa que há atraso é a CONTAGEM no rótulo: "Atrasadas 3" interrompe a
   * varredura sem mexer no lugar onde a tela abre. É o mesmo princípio das abas
   * do Cliente 360 — a contagem é metade da resposta.
   */
  const [aba, definirAba] = useState<ChaveDeGrupo>('hoje');
  const barraDasAbas = useRef<HTMLElement>(null);

  const [soMinhas, definirSoMinhas] = useState(true);
  const [incluirConcluidas, definirIncluirConcluidas] = useState(false);

  /*
   * O "Só em atraso" SAIU, e a aba ocupou o lugar dele.
   *
   * Ele mandava `somente_atrasadas` ao servidor; a aba separa as mesmas tarefas
   * na tela. Os dois juntos davam um estado sem saída: filtro ligado mais aba
   * "Hoje" devolve lista vazia, e nada na tela explica por quê.
   *
   * Os dois que ficaram são de outro eixo — de quem é, e o que já foi feito —
   * e por isso continuam se combinando com qualquer aba.
   */
  const filtro = useMemo(
    () => ({
      ...(soMinhas ? { responsavel: 'eu' } : {}),
      status: incluirConcluidas ? 'TODO,IN_PROGRESS,DONE' : 'TODO,IN_PROGRESS',
    }),
    [soMinhas, incluirConcluidas],
  );

  const consulta = useTarefas(filtro);
  const conclusao = useConclusao(api, invalidar);

  const dados = consulta.data;
  const grupos = useMemo(() => agruparPorPrazo(dados?.itens ?? []), [dados]);
  const escolhido = grupos.find((g) => g.chave === aba) ?? grupos[0]!;

  /*
   * A tarefa recém-criada, até a lista mostrá-la.
   *
   * A tarefa nasce no motor e esta lista lê da projeção `read.tasks`, que só
   * existe depois do evento — ver `espera-da-projecao.ts`. Sem esperar, a
   * rebusca do `onSuccess` chega antes do evento e a lista fica igual.
   *
   * Pode ser que ela nunca apareça AQUI, e está certo: o recorte é do
   * servidor, e uma tarefa fora do filtro escolhido não entra nesta lista. O
   * teto da espera é o que encerra esse caso sem ninguém precisar tratá-lo.
   */
  const [recemCriada, definirRecemCriada] = useState<string | null>(null);

  useEffect(() => {
    if (!recemCriada || !dados) return;
    if (dados.itens.some((t) => t.referencia_externa === recemCriada)) definirRecemCriada(null);
  }, [dados, recemCriada]);

  useEsperaDaProjecao([chaves.tarefas()], recemCriada !== null);

  /*
   * A aba escolhida é trazida para a VISTA na barra que rola.
   *
   * Em 390px as quatro não cabem: tocar em "Próximos dias" trocaria o painel e
   * deixaria a aba escolhida fora da tela, com a barra mostrando três e nenhuma
   * marcada.
   *
   * `scrollLeft`, e nunca `scrollIntoView`: este rola o ancestral junto, e o
   * cabeçalho — que é fixo de propósito — sairia do lugar.
   */
  useEffect(() => {
    const barra = barraDasAbas.current;
    const ativa = barra?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!barra || !ativa) return;
    barra.scrollLeft = Math.max(0, ativa.offsetLeft - (barra.clientWidth - ativa.offsetWidth) / 2);
  }, [aba]);

  return (
    <div className="tar">
      <CabecalhoDeTela
        titulo="Tarefas"
        apoio="O que ficou combinado, e com quem. O prazo manda na ordem; a prioridade ordena dentro do dia."
        acoes={
          <>
            {/*
              Os filtros na MESMA fileira do "Nova tarefa".

              Eles FILTRAM, e no servidor: o recorte vai na consulta, então a
              contagem do escopo e a paginação continuam certas. E são de OUTRO
              eixo que as abas — de quem é a tarefa, e o que já foi feito — por
              isso se combinam com qualquer uma delas.
            */}
            <div className="tar__filtros" role="group" aria-label="Recorte das tarefas">
              <FiltroPilula ligado={soMinhas} aoAlternar={() => definirSoMinhas((v) => !v)}>
                Só as minhas
              </FiltroPilula>
              <FiltroPilula
                ligado={incluirConcluidas}
                aoAlternar={() => definirIncluirConcluidas((v) => !v)}
              >
                Mostrar concluídas
              </FiltroPilula>
            </div>

            {/*
              Criar a partir DAQUI nasce sem vínculo, e é legítimo: é o lembrete
              que não é de cliente nenhum. Quando há contexto — a ficha de um
              cliente, uma conversa — o formulário é aberto de lá, já
              preenchido, porque vincular depois é o caminho que ninguém
              completa.
            */}
            <Botao variante="primario" onClick={() => definirCriando(true)}>
              Nova tarefa
            </Botao>
          </>
        }
      />

      {/*
        As abas são os GRUPOS por prazo.

        Em colunas lado a lado, quatro grupos disputavam a largura e cada um
        ficava com um quarto dela: o cartão aberto espremia a descrição, e no
        celular tudo virava uma pilha de quatro títulos. Como abas, o grupo
        escolhido tem a tela inteira — e a contagem no rótulo diz o que há nos
        outros sem precisar ir até eles.
      */}
      <nav className="tar__abas" ref={barraDasAbas}>
        <div className="tar__abas-trilho" role="tablist" aria-label="Quando fazer">
          {grupos.map((grupo) => (
            <button
              key={grupo.chave}
              type="button"
              role="tab"
              id={`tar-aba-${grupo.chave}`}
              aria-selected={aba === grupo.chave}
              aria-controls="tar-painel"
              className={
                grupo.chave === 'atrasadas' && grupo.itens.length > 0
                  ? 'tar__aba tar__aba--atrasadas'
                  : 'tar__aba'
              }
              onClick={() => definirAba(grupo.chave)}
            >
              {grupo.nome}
              {grupo.itens.length > 0 && (
                <span className="tar__aba-contagem">{grupo.itens.length}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <div
        className="tar__corpo"
        id="tar-painel"
        role="tabpanel"
        aria-labelledby={`tar-aba-${aba}`}
      >
        <div className="tar__painel">
          {dados?.frescor.stale && (
            <Defasado
              projetadoEm={dados.frescor.projetado_em}
              origem="Esta lista"
              aoAtualizar={() => void consulta.refetch()}
            />
          )}

          {conclusao.erro != null && (
            <Erro erro={conclusao.erro} titulo="Não foi possível concluir a tarefa" />
          )}

          <Conteudo
            consulta={consulta}
            grupo={escolhido}
            pendentes={conclusao.pendentes}
            aoConcluir={conclusao.concluir}
            abertaId={abertaId}
            aoAbrir={definirAbertaId}
            aoAbrirConversa={aoAbrirConversa}
            aoAbrirCliente={aoAbrirCliente}
          />
        </div>
      </div>

      {criando && (
        <NovaTarefa
          deOnde="Criada na tela de Tarefas"
          aoFechar={() => definirCriando(false)}
          aoCriar={definirRecemCriada}
        />
      )}

      <AvisoDeDesfazer pendentes={conclusao.pendentes} aoDesfazer={conclusao.desfazer} />
    </div>
  );
}

function Conteudo({
  consulta,
  grupo,
  pendentes,
  aoConcluir,
  abertaId,
  aoAbrir,
  aoAbrirConversa,
  aoAbrirCliente,
}: {
  consulta: ReturnType<typeof useTarefas>;
  grupo: Grupo;
  pendentes: readonly ConclusaoPendente[];
  aoConcluir: (tarefa: TarefaDaApi) => void;
  abertaId: string | null;
  aoAbrir: (id: string | null) => void;
  aoAbrirConversa: (conversaId: string) => void;
  aoAbrirCliente: (clienteId: string) => void;
}): ReactNode {
  if (consulta.isPending) {
    return <Carregando forma="lista" linhas={6} rotulo="Carregando tarefas" />;
  }

  // Só sem nada na tela: com dados carregados, uma atualização que falhou
  // (o limite de requisições, a rede) não apaga o que estava certo.
  if (consulta.error && consulta.data === undefined) {
    return (
      <Erro
        erro={consulta.error}
        aoRepetir={() => void consulta.refetch()}
        titulo="Não foi possível carregar as tarefas"
      />
    );
  }

  if ((consulta.data?.itens.length ?? 0) === 0) {
    return (
      <Vazio
        titulo="Nenhuma tarefa aqui"
        porque="Tarefas criadas a partir de uma conversa ou de uma oportunidade aparecem nesta lista."
      />
    );
  }

  /*
   * O vazio da ABA é diferente do vazio da tela.
   *
   * "Nenhuma tarefa aqui" responde "não há trabalho nenhum"; "Nada atrasado"
   * responde "há trabalho, e nada dele está em atraso". Um texto genérico nas
   * duas apagaria a diferença entre o começo do uso e o estado que se quer ver.
   */
  if (grupo.itens.length === 0) {
    return <p className="tar__grupo-vazio">{grupo.vazio}</p>;
  }

  return (
    <ul className="tar__lista">
      {grupo.itens.map((tarefa) => (
        <CartaoDeTarefa
          key={tarefa.referencia_externa}
          tarefa={tarefa}
          concluida={pareceConcluida(tarefa, pendentes)}
          aberta={tarefa.id !== null && tarefa.id === abertaId}
          aoAlternar={(aberta) => aoAbrir(aberta ? tarefa.id : null)}
          aoConcluir={aoConcluir}
          aoAbrirConversa={aoAbrirConversa}
          aoAbrirCliente={aoAbrirCliente}
        />
      ))}
    </ul>
  );
}

/**
 * O aviso de desfazer.
 *
 * Uma faixa fixa no rodapé, não um alerta por tarefa: concluir três seguidas
 * produziria três avisos empilhados, e o terceiro cobriria o botão do primeiro.
 */
function AvisoDeDesfazer({
  pendentes,
  aoDesfazer,
}: {
  pendentes: readonly ConclusaoPendente[];
  aoDesfazer: (id: string) => void;
}): ReactNode {
  const [agora, definirAgora] = useState(() => Date.now());

  useEffect(() => {
    if (pendentes.length === 0) return;
    const relogio = setInterval(() => definirAgora(Date.now()), 500);
    return () => clearInterval(relogio);
  }, [pendentes.length]);

  if (pendentes.length === 0) return null;
  const ultima = pendentes[pendentes.length - 1]!;

  return (
    <div className="tar__desfazer" role="status">
      <span>
        {pendentes.length === 1
          ? `“${ultima.titulo}” concluída`
          : `${pendentes.length} tarefas concluídas`}
      </span>
      <button type="button" onClick={() => aoDesfazer(ultima.id)}>
        Desfazer ({segundosRestantes(ultima, agora)})
      </button>
    </div>
  );
}

/**
 * A fila de conclusões, e o relógio que a esvazia.
 *
 * O envio acontece fora do clique de propósito: é isso que dá tempo ao
 * desfazer. Sair da tela esvazia a fila — ver o cabeçalho de `conclusao.ts`.
 */
function useConclusao(
  api: ReturnType<typeof useApi>,
  invalidar: (chaves: readonly (readonly unknown[])[]) => void,
): {
  pendentes: readonly ConclusaoPendente[];
  erro: unknown;
  concluir: (tarefa: TarefaDaApi) => void;
  desfazer: (id: string) => void;
} {
  const [pendentes, definirPendentes] = useState<readonly ConclusaoPendente[]>([]);
  const [erro, definirErro] = useState<unknown>(null);

  const enviar = useCallback(
    (id: string) => {
      if (!api) return;

      void api
        .concluirTarefa({ caminho: { id } })
        .then(() => invalidar([chaves.tarefas(), chaves.timeline()]))
        .catch((falha: unknown) => definirErro(falha));
    },
    [api, invalidar],
  );

  // A ref existe para o efeito de saída poder esvaziar a fila sem depender do
  // valor capturado na montagem.
  const atuais = useRef(pendentes);
  atuais.current = pendentes;

  useEffect(() => {
    if (pendentes.length === 0) return;

    const relogio = setInterval(() => {
      const { enviar: prontas, esperando } = separarProntas(atuais.current, Date.now());
      if (prontas.length === 0) return;

      definirPendentes(esperando);
      for (const pronta of prontas) enviar(pronta.id);
    }, 500);

    return () => clearInterval(relogio);
  }, [pendentes.length, enviar]);

  /**
   * Sair da tela vale como confirmar.
   *
   * A pessoa já viu a tarefa marcada; perder a conclusão por trocar de tela
   * seria a tela desmentindo o que mostrou.
   */
  useEffect(
    () => () => {
      for (const pendente of atuais.current) enviar(pendente.id);
    },
    [enviar],
  );

  return {
    pendentes,
    erro,
    concluir: useCallback((tarefa: TarefaDaApi) => {
      if (!tarefa.id) return;
      definirErro(null);
      definirPendentes((atuais) => agendar(atuais, tarefa.id!, tarefa.titulo, Date.now()));
    }, []),
    desfazer: useCallback((id: string) => {
      definirPendentes((atuais) => desfazer(atuais, id));
    }, []),
  };
}

export { JANELA_DE_DESFAZER_MS };
